import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, RefreshCw } from "lucide-react";
import { api } from "../lib/api";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isAssessment = params.get("flow") === "assessment";
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [booking, setBooking] = useState(null);

  const runPoll = useCallback(() => {
    let tries = 0;
    let active = true;
    setStatus("checking");
    const poll = async () => {
      if (!sessionId) { setStatus("error"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (isAssessment) {
            try {
              const { data: b } = await api.get(`/bookings/by-session/${sessionId}`);
              if (active) setBooking(b);
            } catch { /* ignore */ }
          }
          return;
        }
        if (tries >= MAX_POLLS) { setStatus("pending"); return; }
        tries += 1;
        setTimeout(poll, 2000);
      } catch {
        if (active) setStatus("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, isAssessment]);

  useEffect(() => runPoll(), [runPoll]);

  return (
    <Shell>
      {status === "checking" && (
        <>
          <Loader2 className="h-12 w-12 text-[#c9a961] animate-spin" />
          <h1 className="mt-8 font-head text-3xl font-light">Confirming your payment…</h1>
          <p className="mt-3 text-black/50">Hang tight, this only takes a moment.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-[#10b981]" />
          {isAssessment ? (
            <>
              <h1 className="mt-8 font-head text-4xl font-light">You&apos;re on the calendar.</h1>
              {booking?.pretty && (
                <p className="mt-4 font-head text-xl text-[#1e3d3a]">{booking.pretty}</p>
              )}
              <p className="mt-4 max-w-md text-black/65">Your business assessment is confirmed and your $250 fee is paid. A calendar invite with your video link is on its way — Kalib or John will meet you then.</p>
            </>
          ) : (
            <>
              <h1 className="mt-8 font-head text-4xl font-light">You&apos;re in. Welcome to Elevare.</h1>
              <p className="mt-4 max-w-md text-black/65">Your recovery plan is active. Our team will reach out within one business day to kick off your diagnostic.</p>
            </>
          )}
        </>
      )}
      {status === "pending" && (
        <>
          <Loader2 className="h-12 w-12 text-[#f59e0b]" />
          <h1 className="mt-8 font-head text-3xl font-light">Payment processing</h1>
          <p className="mt-4 max-w-md text-black/65">Your payment is still confirming. You&apos;ll receive an email shortly — no need to pay again.</p>
          <button data-testid="check-again-btn" onClick={runPoll}
            className="mt-8 group flex items-center gap-2 rounded-full bg-[#c9a961] text-black px-6 py-3 text-sm font-semibold hover:bg-white transition-colors duration-300">
            <RefreshCw className="h-4 w-4 transition-transform duration-500 group-hover:rotate-180" /> Check again
          </button>
        </>
      )}
      {status === "error" && (
        <>
          <XCircle className="h-14 w-14 text-[#ef4444]" />
          <h1 className="mt-8 font-head text-3xl font-light">We couldn&apos;t verify this session</h1>
          <p className="mt-4 max-w-md text-black/65">If you were charged, contact us and we&apos;ll sort it immediately.</p>
          <button data-testid="retry-btn" onClick={runPoll}
            className="mt-8 group flex items-center gap-2 rounded-full border border-black/20 px-6 py-3 text-sm text-[#1e3d3a]/80 hover:text-[#1e3d3a] hover:border-black/50 transition-colors duration-300">
            <RefreshCw className="h-4 w-4 transition-transform duration-500 group-hover:rotate-180" /> Try again
          </button>
        </>
      )}
      <BackHome />
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <XCircle className="h-14 w-14 text-black/40" />
      <h1 className="mt-8 font-head text-4xl font-light">Checkout cancelled</h1>
      <p className="mt-4 max-w-md text-black/65">No charge was made. Whenever you&apos;re ready, your recovery plan is waiting.</p>
      <BackHome />
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a] flex items-center justify-center px-6">
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 flex flex-col items-center text-center"
      >
        {children}
      </motion.div>
    </div>
  );
}

function BackHome() {
  return (
    <Link to="/" data-testid="back-home" className="mt-10 group flex items-center gap-2 rounded-full border border-black/20 px-6 py-3 text-sm text-[#1e3d3a]/80 hover:text-[#1e3d3a] hover:border-black/50 transition-colors duration-300">
      <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" /> Back to Elevare
    </Link>
  );
}
